// src/hooks/useBatchPayment.js
import { useState, useCallback } from 'react';
import { PaymentAPI } from '../services/api/paymentAPI';

/**
 * Custom hook for managing batch payment functionality
 * @param {Array} appointments - Array of appointments available for batch payment
 * @param {Object} customerInfo - Customer information
 * @returns {Object} Batch payment state and methods
 */
export const useBatchPayment = (appointments, customerInfo) => {
  const [selectedAppointments, setSelectedAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);

  /**
   * Toggle appointment selection
   */
  const toggleAppointment = useCallback((appointmentId) => {
    setSelectedAppointments(prev => {
      if (prev.includes(appointmentId)) {
        return prev.filter(id => id !== appointmentId);
      } else {
        return [...prev, appointmentId];
      }
    });
  }, []);

  /**
   * Select all appointments
   */
  const selectAll = useCallback(() => {
    if (!appointments) return;
    const allIds = appointments.map(apt => apt.id);
    setSelectedAppointments(allIds);
  }, [appointments]);

  /**
   * Deselect all appointments
   */
  const deselectAll = useCallback(() => {
    setSelectedAppointments([]);
  }, []);

  /**
   * Check if appointment is selected
   */
  const isSelected = useCallback((appointmentId) => {
    return selectedAppointments.includes(appointmentId);
  }, [selectedAppointments]);

  /**
   * Calculate total amount for selected appointments
   */
  const calculateTotal = useCallback(() => {
    if (!appointments || selectedAppointments.length === 0) return 0;

    return appointments
      .filter(apt => selectedAppointments.includes(apt.id))
      .reduce((sum, apt) => {
        const price = parseFloat(apt.price) || 0;
        return sum + price;
      }, 0);
  }, [appointments, selectedAppointments]);

  /**
   * Create batch payment link
   */
  const createBatchPayment = useCallback(async (invoiceItemText = '') => {
    if (selectedAppointments.length === 0) {
      setError('אנא בחר לפחות תור אחד');
      return null;
    }

    if (!customerInfo || !customerInfo.customerName) {
      setError('מידע לקוח חסר');
      return null;
    }

    setLoading(true);
    setError(null);
    setPaymentUrl(null);

    try {
      const batchPaymentData = {
        appointmentIds: selectedAppointments,
        customerName: customerInfo.customerName,
        customerPhone: customerInfo.customerPhone || null,
        customerEmail: customerInfo.customerEmail || null
      };

      // Add custom invoice text if provided
      if (invoiceItemText && invoiceItemText.trim()) {
        batchPaymentData.invoiceItemText = invoiceItemText.trim();
      }

      const response = await PaymentAPI.createBatchPaymentLink(batchPaymentData);

      if (response.success) {
        setPaymentUrl(response.data.paymentUrl);
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to create batch payment link');
      }
    } catch (err) {
      console.error('Error creating batch payment:', err);
      setError(err.message || 'שגיאה ביצירת קישור תשלום');
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedAppointments, customerInfo]);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setSelectedAppointments([]);
    setLoading(false);
    setError(null);
    setPaymentUrl(null);
  }, []);

  return {
    selectedAppointments,
    selectedCount: selectedAppointments.length,
    totalAmount: calculateTotal(),
    loading,
    error,
    paymentUrl,
    toggleAppointment,
    selectAll,
    deselectAll,
    isSelected,
    createBatchPayment,
    reset
  };
};

export default useBatchPayment;
